import { create } from "zustand";
import type { PatternProject, ProjectSummary, RecognitionIssue, RecognitionProgress, RecognitionResult, WorkMode } from "../core/types";
import { cloneProject, findNextNonEmptySelection, recalculatePalette, selectedPendingIndices, withMode } from "../core/pattern";
import { projectRepository } from "../core/database";

export type AppScreen = "home" | "processing" | "review" | "editor" | "settings";

interface HistoryEntry { before: PatternProject; after: PatternProject; label: string; }

interface AppState {
  screen: AppScreen;
  summaries: ProjectSummary[];
  activeProject: PatternProject | null;
  recognition: RecognitionResult | null;
  processing: RecognitionProgress | null;
  processingError: string | null;
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];
  toast: string | null;
  setScreen: (screen: AppScreen) => void;
  setToast: (toast: string | null) => void;
  setProcessing: (processing: RecognitionProgress | null) => void;
  setProcessingError: (message: string | null) => void;
  setRecognition: (recognition: RecognitionResult | null) => void;
  loadProjects: () => Promise<void>;
  openProject: (id: string) => Promise<void>;
  acceptRecognition: () => Promise<void>;
  resolveIssue: (issueId: string, code?: string) => void;
  replaceRecognitionProject: (project: PatternProject) => void;
  deleteProject: (id: string) => Promise<void>;
  applyProject: (project: PatternProject, label: string) => void;
  setMode: (mode: WorkMode) => void;
  moveSelection: (direction: 1 | -1) => void;
  setSelection: (selection: number) => void;
  completeSelection: () => void;
  toggleCell: (index: number) => void;
  setCellPalette: (index: number, paletteId: number) => void;
  undo: () => void;
  redo: () => void;
  saveActive: () => Promise<void>;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleSave(project: PatternProject) {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => { void projectRepository.update(project); }, 250);
}

function updated(project: PatternProject): PatternProject {
  return { ...project, updatedAt: new Date().toISOString() };
}

export const useAppStore = create<AppState>((set, get) => ({
  screen: "home",
  summaries: [],
  activeProject: null,
  recognition: null,
  processing: null,
  processingError: null,
  undoStack: [],
  redoStack: [],
  toast: null,
  setScreen: (screen) => {
    if (screen === "home") localStorage.removeItem("pindou:last-project");
    set({ screen });
  },
  setToast: (toast) => set({ toast }),
  setProcessing: (processing) => set({ processing }),
  setProcessingError: (processingError) => set({ processingError }),
  setRecognition: (recognition) => set({ recognition }),
  async loadProjects() { set({ summaries: await projectRepository.list() }); },
  async openProject(id) {
    const activeProject = await projectRepository.get(id);
    if (activeProject) {
      localStorage.setItem("pindou:last-project", id);
      set({ activeProject, screen: "editor", undoStack: [], redoStack: [] });
    }
  },
  async acceptRecognition() {
    const recognition = get().recognition;
    if (!recognition) return;
    if (recognition.issues.some((issue) => !issue.resolved)) {
      set({ toast: "请先确认所有疑点" });
      return;
    }
    await projectRepository.create(recognition.project, recognition.sourceBlob, recognition.previewBlob);
    localStorage.setItem("pindou:last-project", recognition.project.id);
    set({ activeProject: recognition.project, screen: "editor", recognition: null, undoStack: [], redoStack: [] });
    await get().loadProjects();
  },
  resolveIssue(issueId, code) {
    const recognition = get().recognition;
    if (!recognition) return;
    const selected = recognition.issues.find((issue) => issue.id === issueId);
    const issues: RecognitionIssue[] = recognition.issues.map((issue) => issue.id === issueId ? { ...issue, resolved: true, candidates: code ? [code] : issue.candidates } : issue);
    set({ recognition: { ...recognition, issues, totalFromHeader: selected?.kind === "count" ? recognition.totalFromCells : recognition.totalFromHeader } });
  },
  replaceRecognitionProject(project) {
    const recognition = get().recognition;
    if (recognition) set({ recognition: { ...recognition, project: recalculatePalette(project), totalFromCells: project.cells.reduce((sum, value) => sum + Number(value !== 0), 0) } });
  },
  async deleteProject(id) {
    await projectRepository.delete(id);
    if (get().activeProject?.id === id) set({ activeProject: null, screen: "home" });
    await get().loadProjects();
  },
  applyProject(project, label) {
    const before = get().activeProject;
    if (!before) return;
    const after = updated(project);
    const undoStack = [...get().undoStack, { before: cloneProject(before), after: cloneProject(after), label }].slice(-50);
    set({ activeProject: after, undoStack, redoStack: [] });
    scheduleSave(after);
  },
  setMode(mode) {
    const project = get().activeProject;
    if (project) get().applyProject(withMode(project, mode), `切换为${mode}模式`);
  },
  moveSelection(direction) {
    const project = get().activeProject;
    if (!project) return;
    const selection = findNextNonEmptySelection(project, direction);
    const next = { ...project, currentSelection: selection };
    if (next.currentMode === "color") next.selectedColorId = next.palette[selection]?.id ?? null;
    set({ activeProject: updated(next) });
    scheduleSave(next);
  },
  setSelection(selection) {
    const project = get().activeProject;
    if (!project) return;
    const next = updated({ ...project, currentSelection: selection });
    if (next.currentMode === "color") next.selectedColorId = next.palette[selection]?.id ?? null;
    set({ activeProject: next });
    scheduleSave(next);
  },
  completeSelection() {
    const project = get().activeProject;
    if (!project) return;
    const indices = selectedPendingIndices(project);
    if (!indices.length) { set({ toast: "当前区域已完成" }); return; }
    const next = cloneProject(project);
    for (const index of indices) next.completed[index] = 1;
    get().applyProject(next, "完成当前区域");
  },
  toggleCell(index) {
    const project = get().activeProject;
    if (!project || !project.cells[index]) return;
    const next = cloneProject(project);
    next.completed[index] = next.completed[index] === 1 ? 0 : 1;
    get().applyProject(next, "切换单格进度");
  },
  setCellPalette(index, paletteId) {
    const project = get().activeProject;
    if (!project || index < 0 || index >= project.cells.length) return;
    const next = cloneProject(project);
    next.cells[index] = paletteId;
    get().applyProject(recalculatePalette(next), "修正单格色号");
  },
  undo() {
    const undoStack = get().undoStack;
    const entry = undoStack.at(-1);
    if (!entry) return;
    const activeProject = cloneProject(entry.before);
    set({ activeProject, undoStack: undoStack.slice(0, -1), redoStack: [...get().redoStack, entry] });
    scheduleSave(activeProject);
  },
  redo() {
    const redoStack = get().redoStack;
    const entry = redoStack.at(-1);
    if (!entry) return;
    const activeProject = cloneProject(entry.after);
    set({ activeProject, redoStack: redoStack.slice(0, -1), undoStack: [...get().undoStack, entry] });
    scheduleSave(activeProject);
  },
  async saveActive() {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    const project = get().activeProject;
    if (project) await projectRepository.update(project);
  }
}));

export async function flushProjectSave(): Promise<void> { await useAppStore.getState().saveActive(); }
